import { useRef, useState } from 'react';
import { RefreshCw, Settings, Database, Loader2, MoreHorizontal, Sparkles } from 'lucide-react';
import { PageToolbar } from '../../components/ui/PageLayout';
import { FeatureGate } from '../../contexts/FeatureFlagContext';
import AnchorDropdownPanel from '../../components/ui/AnchorDropdownPanel';

const menuItemClass = 'nav-dropdown-item-mdl inline-flex w-full items-center gap-2';

/**
 * Repository detail page toolbar: sync, Vanti, and more-actions menu.
 */
export default function RepositoryDetailToolbar({
  repoData,
  breadcrumbItems,
  isSyncingNow,
  canCancelSync,
  onSync,
  onStopSync,
  onOpenSettings,
  canTriggerCodeIndex,
  repository,
  codeIndexTriggering,
  onTriggerCodeIndex,
  deleting,
  onDelete,
}: any) {
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef<any>(null);
  const menuId = 'repo-detail-more-actions';

  function closeAnd(fn) {
    return () => {
      setMoreMenuOpen(false);
      fn();
    };
  }

  return (
    <PageToolbar title={repoData.fullName} breadcrumbItems={breadcrumbItems || undefined}>
      <button
        type="button"
        onClick={() => (isSyncingNow ? onStopSync() : onSync())}
        disabled={isSyncingNow && !canCancelSync}
        className="btn-secondary-mdl inline-flex items-center gap-2"
        title={
          isSyncingNow
            ? 'Stop sync (may finish the current step first)'
            : 'Sync repository from GitHub'
        }
      >
        <RefreshCw className={`w-4 h-4 ${isSyncingNow ? 'animate-spin' : ''}`} />
        {isSyncingNow ? (canCancelSync ? 'Stop sync' : 'Syncing...') : 'Sync Now'}
      </button>
      <FeatureGate flag="ui.ai.chat">
        <button
          type="button"
          onClick={() =>
            window.dispatchEvent(
              new CustomEvent('vanti:ask', {
                detail: {
                  question: `Summarise activity and health for repository ${repoData.fullName}`,
                },
              })
            )
          }
          className="btn-secondary-mdl inline-flex items-center justify-center"
          aria-label="Ask Vanti about this repo"
          title="Ask Vanti about this repo"
        >
          <Sparkles className="w-4 h-4 text-vivid-green-cyan" aria-hidden />
        </button>
      </FeatureGate>
      <div className="relative">
        <button
          ref={moreMenuRef}
          type="button"
          onClick={() => setMoreMenuOpen((open) => !open)}
          className="btn-secondary-mdl inline-flex items-center justify-center"
          aria-label="More actions"
          aria-expanded={moreMenuOpen}
          aria-haspopup="menu"
          aria-controls={menuId}
        >
          <MoreHorizontal className="w-4 h-4" aria-hidden />
        </button>
        <AnchorDropdownPanel
          id={menuId}
          open={moreMenuOpen}
          onClose={() => setMoreMenuOpen(false)}
          anchorRef={moreMenuRef}
          ariaLabel="More actions"
          panelWidth={180}
        >
          <div className="-m-4 py-1" role="menu">
            <button
              type="button"
              role="menuitem"
              onClick={closeAnd(onOpenSettings)}
              className={menuItemClass}
            >
              <Settings className="w-4 h-4 shrink-0" aria-hidden />
              Settings
            </button>
            {canTriggerCodeIndex && repository?.projectId && !repository.disconnectedAt ? (
              <button
                type="button"
                role="menuitem"
                onClick={closeAnd(onTriggerCodeIndex)}
                disabled={codeIndexTriggering}
                className={`${menuItemClass} disabled:opacity-50`}
                title="Queue embedding of the default branch for search / Portfolios (runs in the background)"
              >
                {codeIndexTriggering ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                ) : (
                  <Database className="h-4 w-4 shrink-0" aria-hidden />
                )}
                Index code
              </button>
            ) : null}
            <hr className="my-1 border-dark-border" />
            <button
              type="button"
              role="menuitem"
              onClick={closeAnd(onDelete)}
              disabled={deleting}
              className={`${menuItemClass} text-red-400 disabled:opacity-50`}
            >
              {deleting ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </div>
        </AnchorDropdownPanel>
      </div>
    </PageToolbar>
  );
}
